import { useEffect, useMemo } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import Footer from '../../components/Footer';
import PageHeader from '../../components/PageHeader';
import useAdoptionForm from '../../hooks/useAdoptionForm';
import {
  PRIORITY_COUNTRIES,
  OTHER_COUNTRIES,
  getCountryOptions,
  filterCountryOption,
} from '../../utils/countries';

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '48px',
    height: '48px',
    borderRadius: '12px',
    borderColor: state.isFocused ? '#F9BE4F' : '#675444',
    borderWidth: '2px',
    borderStyle: 'solid',
    boxShadow: 'none',
    backgroundColor: '#fff',
    '&:hover': {
      borderColor: state.isFocused ? '#F9BE4F' : '#675444',
    },
    cursor: 'pointer',
    fontSize: '15px',
  }),
  valueContainer: base => ({
    ...base,
    padding: '0 14px',
    height: '44px',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    color: '#333',
  }),
  placeholder: base => ({
    ...base,
    color: '#777',
    fontSize: '15px',
  }),
  singleValue: base => ({
    ...base,
    color: '#333',
    fontWeight: 500,
    fontSize: '15px',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: '#675444',
    paddingRight: '12px',
    transition: 'transform 0.2s ease',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
    '&:hover': {
      color: '#F9BE4F',
    },
  }),
  clearIndicator: base => ({
    ...base,
    color: '#999',
    '&:hover': {
      color: '#dc3545',
    },
  }),
  menu: base => ({
    ...base,
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 10px 25px rgba(103, 84, 68, 0.18)',
    border: '1px solid #e2dbd5',
    zIndex: 9999,
  }),
  menuList: base => ({
    ...base,
    padding: '6px',
    maxHeight: '260px',
  }),
  groupHeading: base => ({
    ...base,
    color: '#675444',
    fontWeight: 700,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    backgroundColor: '#FBF4DE',
    padding: '6px 12px',
    borderRadius: '6px',
    margin: '4px 0 2px 0',
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: '8px',
    padding: '9px 14px',
    fontSize: '14px',
    cursor: 'pointer',
    backgroundColor: state.isSelected
      ? '#675444'
      : state.isFocused
        ? '#FBF4DE'
        : 'transparent',
    color: state.isSelected ? '#ffffff' : '#333333',
    fontWeight: state.isSelected ? 600 : 400,
    '&:active': {
      backgroundColor: '#F9BE4F',
      color: '#333333',
    },
  }),
};

function AdoptionForm() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const petNameParam = searchParams.get('petName') || '';

  const {
    form,
    setForm,
    loading,
    error,
    success,
    handleChange,
    sendEmail,
    setError,
    setSuccess,
  } = useAdoptionForm();

  const { executeRecaptcha } = useGoogleReCaptcha();

  // Prefill pet name if passed via query params
  useEffect(() => {
    if (petNameParam && !form.which_dog) {
      setForm(prev => ({ ...prev, which_dog: petNameParam }));
    }
  }, [petNameParam]);

  // Timeout for messages
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success, setError, setSuccess]);

  const countryOptions = useMemo(() => {
    return getCountryOptions(i18n.language, {
      common_countries: t('adoptionForm.the_most_common'),
      priority_countries: t('adoptionForm.the_most_common'),
      other_countries: t('adoptionForm.other_countries'),
    });
  }, [i18n.language, t]);

  const selectedCountryOption = useMemo(() => {
    if (!form.country_code && !form.country) return null;
    for (const group of countryOptions) {
      const match = group.options.find(
        opt => opt.value === form.country_code || opt.label === form.country
      );
      if (match) return match;
    }
    return null;
  }, [countryOptions, form.country_code, form.country]);

  // Keep country name in form in sync with active language
  useEffect(() => {
    if (form.country_code) {
      const isBs =
        !i18n.language || i18n.language.toLowerCase().startsWith('bs');
      const allCountries = [...PRIORITY_COUNTRIES, ...OTHER_COUNTRIES];
      const match = allCountries.find(c => c.code === form.country_code);
      if (match) {
        const localizedName = isBs ? match.nameBs : match.nameEn;
        if (form.country !== localizedName) {
          setForm(prev => ({
            ...prev,
            country: localizedName,
          }));
        }
      }
    }
  }, [i18n.language, form.country_code, form.country, setForm]);

  const handleCountryChange = selected => {
    const isBs = !i18n.language || i18n.language.toLowerCase().startsWith('bs');
    const localizedName = selected
      ? isBs
        ? selected.nameBs
        : selected.nameEn
      : '';
    const code = selected ? selected.value : '';
    setForm(prev => ({
      ...prev,
      country: localizedName,
      country_code: code,
    }));
  };

  const getInput = (name, type = 'text', required = true) => (
    <input
      type={type}
      name={name}
      className="form-control input-field mb-3"
      required={required}
      value={form[name] || ''}
      onChange={handleChange}
    />
  );

  const getTextarea = (name, required = true) => (
    <textarea
      name={name}
      className="textarea-field form-control mb-3"
      rows="3"
      required={required}
      value={form[name] || ''}
      onChange={handleChange}
    ></textarea>
  );

  const labels = {
    name: t('adoptionForm.name_surname'),
    country: t('adoptionForm.country'),
    city: t('adoptionForm.city'),
    address_neighborhood: t('adoptionForm.address_neighborhood'),
    email: t('adoptionForm.email'),
    phone: t('adoptionForm.phone'),
    year_of_birth: t('adoptionForm.year_of_birth'),
    apartment_or_house: t('adoptionForm.apartment_or_house'),
    property_owned: t('adoptionForm.property_owned'),
    yard_garden: t('adoptionForm.yard_garden'),
    occupation: t('adoptionForm.occupation'),
    hours_away: t('adoptionForm.hours_away'),
    had_dog: t('adoptionForm.had_dog'),
    other_animals: t('adoptionForm.other_animals'),
    household_people: t('adoptionForm.household_people'),
    time_devoted: t('adoptionForm.time_devoted'),
    sleeping_place: t('adoptionForm.sleeping_place'),
    why_adopt: t('adoptionForm.why_adopt'),
    which_dog: t('adoptionForm.which_dog'),
    vet_costs: t('adoptionForm.vet_costs'),
    behavioral_problems: t('adoptionForm.behavioral_problems'),
    vacation: t('adoptionForm.vacation'),
    life_changes: t('adoptionForm.life_changes'),
    how_soon: t('adoptionForm.how_soon'),
    meet_saturday: t('adoptionForm.meet_saturday'),
    sign_contract: t('adoptionForm.sign_contract'),
    send_updates: t('adoptionForm.send_updates'),
    ready_to_sterilize: t('adoptionForm.ready_to_sterilize'),
  };

  return (
    <div id="top">
      <PageHeader
        title={t('adoptionForm.title')}
        page={t('adoptionForm.page')}
        pageUrl={'adoption-form'}
      />

      <div className="page">
        <div className="container">
          <div className="row">
            {/* FORM SIDE */}
            <div className="contact-info col-lg-8 offset-lg-2">
              <h4 className="mb-4">{t('adoptionForm.title_send_message')}</h4>

              <form
                id="adoption_form"
                onSubmit={async e => {
                  e.preventDefault();

                  if (!executeRecaptcha) {
                    return;
                  }

                  const token = await executeRecaptcha('adoption_form');
                  sendEmail(token, labels);
                }}
              >
                {/* SECTION 1: Basic Info */}
                <h5 className="mt-5 mb-3">{t('adoptionForm.basic_info')}</h5>
                <div className="row">
                  <div className="col-md-6">
                    <label>
                      {t('adoptionForm.name_surname')}
                      <span className="required">*</span>
                    </label>
                    {getInput('name')}
                  </div>
                  <div className="col-md-6">
                    <label>
                      {t('adoptionForm.email')}
                      <span className="required">*</span>
                    </label>
                    {getInput('email', 'email')}
                  </div>
                  <div className="col-md-6">
                    <label>
                      {t('adoptionForm.year_of_birth')}
                      <span className="required">*</span>
                    </label>
                    {getInput('year_of_birth', 'number')}
                  </div>
                  <div className="col-md-6">
                    <label>
                      {t('adoptionForm.phone')}
                      <span className="required">*</span>
                    </label>
                    {getInput('phone')}
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="countrySelectInput">
                      {t('adoptionForm.country')}
                      <span className="required">*</span>
                    </label>
                    <Select
                      id="countrySelect"
                      inputId="countrySelectInput"
                      name="country"
                      options={countryOptions}
                      value={selectedCountryOption}
                      onChange={handleCountryChange}
                      filterOption={filterCountryOption}
                      styles={customSelectStyles}
                      className="react-select-container mb-3"
                      classNamePrefix="react-select"
                      placeholder={t('adoptionForm.select_country')}
                      isClearable
                    />
                    <input
                      tabIndex={-1}
                      autoComplete="off"
                      style={{
                        opacity: 0,
                        width: 0,
                        height: 0,
                        position: 'absolute',
                        pointerEvents: 'none',
                      }}
                      value={form.country || ''}
                      required
                      onChange={() => {}}
                    />
                  </div>
                  <div className="col-md-6">
                    <label>
                      {t('adoptionForm.city')}
                      <span className="required">*</span>
                    </label>
                    {getInput('city')}
                  </div>
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.address_neighborhood')}
                      <span className="required">*</span>
                    </label>
                    {getInput('address_neighborhood')}
                  </div>
                </div>

                {/* SECTION 2: About you and your home */}
                <h5 className="mt-4 mb-3">{t('adoptionForm.about_home')}</h5>
                <div className="row">
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.apartment_or_house')}
                      <span className="required">*</span>
                    </label>
                    {getInput('apartment_or_house')}
                  </div>
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.property_owned')}
                      <span className="required">*</span>
                    </label>
                    {getInput('property_owned')}
                  </div>
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.yard_garden')}
                      <span className="required">*</span>
                    </label>
                    {getInput('yard_garden')}
                  </div>
                </div>

                {/* SECTION 3: Work and time */}
                <h5 className="mt-4 mb-3">{t('adoptionForm.work_time')}</h5>
                <div className="row">
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.occupation')}
                      <span className="required">*</span>
                    </label>
                    {getInput('occupation')}
                  </div>
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.hours_away')}
                      <span className="required">*</span>
                    </label>
                    {getInput('hours_away')}
                  </div>
                </div>

                {/* SECTION 4: Experience */}
                <h5 className="mt-4 mb-3">{t('adoptionForm.experience')}</h5>
                <div className="row">
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.had_dog')}
                      <span className="required">*</span>
                    </label>
                    {getTextarea('had_dog')}
                  </div>
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.other_animals')}
                      <span className="required">*</span>
                    </label>
                    {getTextarea('other_animals')}
                  </div>
                </div>

                {/* SECTION 5: Family */}
                <h5 className="mt-4 mb-3">{t('adoptionForm.family')}</h5>
                <div className="row">
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.household_people')}
                      <span className="required">*</span>
                    </label>
                    {getTextarea('household_people')}
                  </div>
                </div>

                {/* SECTION 6: Time and care */}
                <h5 className="mt-4 mb-3">{t('adoptionForm.time_care')}</h5>
                <div className="row">
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.time_devoted')}
                      <span className="required">*</span>
                    </label>
                    {getTextarea('time_devoted')}
                  </div>
                </div>

                {/* SECTION 7: Dog life */}
                <h5 className="mt-4 mb-3">{t('adoptionForm.dog_life')}</h5>
                <div className="row">
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.sleeping_place')}
                      <span className="required">*</span>
                    </label>
                    {getTextarea('sleeping_place')}
                  </div>
                </div>

                {/* SECTION 8: Motivation */}
                <h5 className="mt-4 mb-3">{t('adoptionForm.motivation')}</h5>
                <div className="row">
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.why_adopt')}
                      <span className="required">*</span>
                    </label>
                    {getTextarea('why_adopt')}
                  </div>
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.which_dog')}
                      <span className="required">*</span>
                    </label>
                    {getInput('which_dog')}
                  </div>
                </div>

                {/* SECTION 9: Responsibility */}
                <h5 className="mt-4 mb-3">
                  {t('adoptionForm.responsibility')}
                </h5>
                <div className="row">
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.vet_costs')}
                      <span className="required">*</span>
                    </label>
                    {getTextarea('vet_costs')}
                  </div>
                </div>

                {/* SECTION 10: Additional questions */}
                <h5 className="mt-4 mb-3">
                  {t('adoptionForm.additional_questions')}
                </h5>
                <div className="row">
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.behavioral_problems')}
                      <span className="required">*</span>
                    </label>
                    {getTextarea('behavioral_problems')}
                  </div>
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.vacation')}
                      <span className="required">*</span>
                    </label>
                    {getTextarea('vacation')}
                  </div>
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.life_changes')}
                      <span className="required">*</span>
                    </label>
                    {getTextarea('life_changes')}
                  </div>
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.how_soon')}
                      <span className="required">*</span>
                    </label>
                    {getInput('how_soon')}
                  </div>
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.meet_saturday')}
                      <span className="required">*</span>
                    </label>
                    {getInput('meet_saturday')}
                  </div>
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.sign_contract')}
                      <span className="required">*</span>
                    </label>
                    {getTextarea('sign_contract')}
                  </div>
                  <div className="col-md-12">
                    <label>
                      {t('adoptionForm.send_updates')}
                      <span className="required">*</span>
                    </label>
                    {getTextarea('send_updates')}
                  </div>
                  <div className="col-md-12 mt-3">
                    <label style={{ display: 'block', marginBottom: '10px' }}>
                      {t('adoptionForm.ready_to_sterilize')}
                      <span className="required">*</span>
                    </label>
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '32px',
                        alignItems: 'center',
                      }}
                    >
                      <label
                        htmlFor="sterilize_yes"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontWeight: 'normal',
                          cursor: 'pointer',
                          marginBottom: 0,
                          fontSize: '16px',
                        }}
                      >
                        <input
                          type="radio"
                          name="ready_to_sterilize"
                          id="sterilize_yes"
                          value="Yes"
                          required
                          checked={form.ready_to_sterilize === 'Yes'}
                          onChange={handleChange}
                          style={{
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer',
                            flexShrink: 0,
                          }}
                        />
                        {t('adoptionForm.yes_radio')}
                      </label>
                      <label
                        htmlFor="sterilize_no"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontWeight: 'normal',
                          cursor: 'pointer',
                          marginBottom: 0,
                          fontSize: '16px',
                        }}
                      >
                        <input
                          type="radio"
                          name="ready_to_sterilize"
                          id="sterilize_no"
                          value="No"
                          required
                          checked={form.ready_to_sterilize === 'No'}
                          onChange={handleChange}
                          style={{
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer',
                            flexShrink: 0,
                          }}
                        />
                        {t('adoptionForm.no_radio')}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    type="submit"
                    id="submit_btn"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading
                      ? t('adoptionForm.sending')
                      : t('adoptionForm.send_message')}
                  </button>
                </div>

                <div id="contact_results" className="mt-3">
                  {error && (
                    <p style={{ color: 'red' }}>
                      {t('contactPage.error_message')}
                    </p>
                  )}
                  {success && (
                    <p style={{ color: 'green' }}>
                      {t('contactPage.success_message')}
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default AdoptionForm;
